/**
 * Is the store's version newer than the one running? (D266 §3.2)
 *
 * **The comparison is in TypeScript rather than in Swift, and that is the deviation this module
 * exists to be.** The obvious reading is that `SeloGateStoreUpdatePlugin.swift` compares the two
 * versions itself — the lookup response and `CFBundleShortVersionString` are both right there. It
 * should not, for one reason: `1.10 > 1.9` is a string comparison away from being wrong, `1.3` and
 * `1.3.0` are the same release spelled two ways, and **nothing in `npm run verify` compiles
 * Swift** — `native/test/` is vitest over source text, so a comparator written there would ship
 * green and stay green through both defects. Here it has a suite.
 *
 * It is also one language rather than two. Android needs no comparator at all — Play answers
 * `UPDATE_AVAILABLE` itself from its own install record — so the only platform that needs this is
 * the one whose plugin `platformRequirements.ts` already refuses to let compose anything
 * (*the plugins compose nothing and inspect nothing*).
 *
 * **The bias is toward the false negative, deliberately.** A version this cannot parse is "not
 * newer", so the row stays absent: a resident who is told nothing has lost only a nudge, while a
 * spurious *Update in App Store* sends them to a listing showing the version they already have,
 * with no way to tell that the app was wrong rather than the store slow. D135's posture about a
 * gate's status, applied to a version string.
 */

/**
 * Numeric components only, and **any non-numeric component makes the whole string unusable.**
 *
 * Apple's `CFBundleShortVersionString` is documented as up to three period-separated integers, and
 * `store-build.json`'s `MARKETING_VERSION` is pinned to that shape by `association.test.ts`. So a
 * `1.4.0-beta.2` or a `v1.4` here is not a version this product mints — it is a sign the field
 * being read is not the field intended, and guessing what it means is how a build number ends up
 * compared against a marketing version.
 */
function componentsOf(version: string): readonly number[] | undefined {
  const trimmed = version.trim()
  if (trimmed === '') return undefined
  const parts = trimmed.split('.')
  const numbers: number[] = []
  for (const part of parts) {
    // `Number('')` is `0` and `Number(' 2 ')` is `2`, so the shape is asserted before the parse:
    // an empty component (`1..3`, `1.`) and a padded one are both malformed rather than lenient.
    if (!/^\d+$/.test(part)) return undefined
    numbers.push(Number(part))
  }
  return numbers
}

/**
 * Whether `store` names a release later than `current`.
 *
 * Component-wise and numeric, so `1.10` beats `1.9`; missing components are padded with zeros, so
 * `1.3` and `1.3.0` are the same release and `1.3.1` beats both. Anything unparseable on either
 * side is `false`.
 */
export function isNewerVersion(store: string, current: string): boolean {
  const left = componentsOf(store)
  const right = componentsOf(current)
  if (left === undefined || right === undefined) return false

  const length = Math.max(left.length, right.length)
  for (let index = 0; index < length; index += 1) {
    const a = left[index] ?? 0
    const b = right[index] ?? 0
    if (a !== b) return a > b
  }
  return false
}
